import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import {
  LayoutDashboard, Users, Boxes, Terminal, History, ShieldCheck,
  Settings, FileText, ScrollText, Search, CornerDownLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCommandPalette } from './commandPaletteAtom';

type Cmd = { id: string; label: string; group: string; icon: React.ReactNode; href?: string; run?: () => void; keywords?: string };

export function CommandPalette() {
  const [open, setOpen] = useCommandPalette();
  const [, navigate] = useLocation();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands: Cmd[] = useMemo(() => [
    { id: 'dashboard', label: 'Go to Dashboard', group: 'Navigate', icon: <LayoutDashboard className="h-4 w-4" />, href: '/' },
    { id: 'console', label: 'Live Console', group: 'Navigate', icon: <Terminal className="h-4 w-4" />, href: '/server/console' },
    { id: 'players', label: 'Players', group: 'Navigate', icon: <Users className="h-4 w-4" />, href: '/players' },
    { id: 'resources', label: 'Resources', group: 'Navigate', icon: <Boxes className="h-4 w-4" />, href: '/server/resources' },
    { id: 'history', label: 'History', group: 'Navigate', icon: <History className="h-4 w-4" />, href: '/history' },
    { id: 'audit', label: 'Audit Trail', group: 'Navigate', icon: <ShieldCheck className="h-4 w-4" />, href: '/audit' },
    { id: 'settings', label: 'Settings', group: 'Navigate', icon: <Settings className="h-4 w-4" />, href: '/settings' },
    { id: 'files', label: 'File Manager', group: 'Navigate', icon: <FileText className="h-4 w-4" />, href: '/files' },
    { id: 'syslog', label: 'Console Log', group: 'Navigate', icon: <ScrollText className="h-4 w-4" />, href: '/system/console-log' },
  ], []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => (c.label + ' ' + (c.keywords ?? '')).toLowerCase().includes(q));
  }, [commands, query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => { setActive(0); }, [query]);

  if (!open) return null;

  const choose = (cmd: Cmd) => {
    setOpen(false);
    if (cmd.href) navigate(cmd.href);
    cmd.run?.();
  };

  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && filtered[active]) { e.preventDefault(); choose(filtered[active]); }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center pt-[12vh] px-4"
      style={{ background: 'rgba(3,5,9,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={() => setOpen(false)}
    >
      <div
        className="sc-glass sc-page-in w-full max-w-xl rounded-2xl overflow-hidden"
        style={{ border: '1px solid var(--sc-border-mid)', boxShadow: 'var(--sc-glow-lg)' }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onListKey}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'var(--sc-border)' }}>
          <Search className="h-4 w-4 shrink-0" style={{ color: 'var(--sc-muted)' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages and actions…"
            className="flex-1 bg-transparent outline-none text-sm"
            style={{ color: 'var(--sc-text)' }}
          />
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'var(--sc-bg-elevated)', color: 'var(--sc-muted)' }}>ESC</kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--sc-muted)' }}>No matches for "{query}"</div>
          ) : filtered.map((cmd, i) => (
            <button
              key={cmd.id}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(cmd)}
              className={cn('w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors')}
              style={{
                background: i === active ? 'var(--sc-bg-overlay)' : 'transparent',
                color: i === active ? 'var(--sc-text)' : 'var(--sc-muted)',
              }}
            >
              <span style={{ color: i === active ? 'var(--sc-cyan)' : 'var(--sc-muted)' }}>{cmd.icon}</span>
              <span className="flex-1">{cmd.label}</span>
              {i === active && <CornerDownLeft className="h-3.5 w-3.5 opacity-60" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
